import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { FaEnvelope, FaExternalLinkAlt, FaArrowLeft } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";

// Tracking params that should never be rendered to the agent.
const TOKEN_PARAMS = ["t", "token", "param_token"];

// Strip ?t= / ?token= / ?param_token= from every URL inside an HTML or
// plain-text body. Mirrors the server-side scrub so we still hide tokens
// if a body slips through unscrubbed (defence in depth).
function stripTokensFromBody(body) {
  if (!body) return body;
  try {
    return body
      .replace(
        /([?&])(?:t|token|param_token)=[^&"'\s<>]*/gi,
        (_m, sep) => (sep === "?" ? "?" : ""),
      )
      .replace(/\?&/g, "?")
      .replace(/[?&](?=["'\s<>]|$)/g, "");
  } catch {
    return body;
  }
}

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString("en-AU", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function recipientList(value) {
  if (!value) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "string" ? v : (v?.Name ? `${v.Name} <${v.Email}>` : v?.Email)))
      .filter(Boolean)
      .join(", ");
  }
  return String(value);
}

export default function EmailDetails() {
  const { messageId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);
  const [view, setView] = useState("html"); // html | text | raw

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/email-details/${encodeURIComponent(messageId)}`,
          {},
        );
        if (cancelled) return;
        const data = res?.data || res;
        setDetails(data);
        if (!data?.HtmlBody && data?.TextBody) setView("text");
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.message || e?.message || "Failed to load email";
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (messageId) load();
    return () => { cancelled = true; };
  }, [messageId]);

  // Defence-in-depth: rescrub HTML on the client too.
  const safeHtml = details?.HtmlBody ? stripTokensFromBody(details.HtmlBody) : "";
  const safeText = details?.TextBody ? stripTokensFromBody(details.TextBody) : "";
  const safeRaw = details?.Body ? stripTokensFromBody(details.Body) : "";

  return (
    <div style={{
      flex: 1, minHeight: 0, overflow: "auto",
      background: "#f6f8fa", padding: "16px 16px 32px",
    }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Header */}
        <div style={{
          background: "#fff", borderRadius: 12, padding: "14px 18px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)", marginBottom: 12,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : window.close()}
            title="Back"
            style={{
              background: "none", border: "1px solid #e5e7eb", borderRadius: 8,
              padding: "6px 10px", cursor: "pointer", color: "#374151",
              display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
            }}
          >
            <FaArrowLeft style={{ fontSize: 11 }} /> Back
          </button>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32, borderRadius: 8,
            background: "#eef2ff", color: "#4338ca",
          }}>
            <FaEnvelope />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
              {details?.Subject || (loading ? "Loading…" : "Email")}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Message ID: <code style={{ fontSize: 11 }}>{messageId}</code>
            </div>
          </div>
        </div>

        {loading && (
          <div style={{
            background: "#fff", borderRadius: 12, padding: 32, textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}>
            <Spinner animation="border" size="sm" /> Loading email…
          </div>
        )}

        {!loading && error && (
          <div style={{
            background: "#fff", borderRadius: 12, padding: 24,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)", color: "#b91c1c",
          }}>
            {error}
          </div>
        )}

        {!loading && !error && details && (
          <>
            {/* Metadata */}
            <div style={{
              background: "#fff", borderRadius: 12, padding: "14px 18px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)", marginBottom: 12,
              fontSize: 13, color: "#374151",
            }}>
              <MetaRow label="From" value={details.From} />
              <MetaRow label="To" value={recipientList(details.To)} />
              {recipientList(details.Cc) && <MetaRow label="Cc" value={recipientList(details.Cc)} />}
              {recipientList(details.Bcc) && <MetaRow label="Bcc" value={recipientList(details.Bcc)} />}
              <MetaRow label="Sent" value={fmtDate(details.ReceivedAt)} />
              {details.Status && <MetaRow label="Status" value={details.Status} />}
              {details.Tag && <MetaRow label="Tag" value={details.Tag} />}
            </div>

            {/* View toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {safeHtml && (
                <ViewTab active={view === "html"} onClick={() => setView("html")}>HTML</ViewTab>
              )}
              {safeText && (
                <ViewTab active={view === "text"} onClick={() => setView("text")}>Text</ViewTab>
              )}
              {safeRaw && (
                <ViewTab active={view === "raw"} onClick={() => setView("raw")}>Raw</ViewTab>
              )}
            </div>

            {/* Body */}
            <div style={{
              background: "#fff", borderRadius: 12, overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}>
              {view === "html" && safeHtml && (
                <iframe
                  title="Email HTML"
                  srcDoc={safeHtml}
                  // sandbox blocks scripts and form submission; allow same-origin
                  // is intentionally omitted so the iframe can't access app state.
                  sandbox=""
                  style={{
                    width: "100%", border: "none", minHeight: "70vh", background: "#fff",
                  }}
                />
              )}
              {view === "text" && safeText && (
                <pre style={{
                  margin: 0, padding: 18, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: 13, color: "#1f2937",
                }}>{safeText}</pre>
              )}
              {view === "raw" && safeRaw && (
                <pre style={{
                  margin: 0, padding: 18, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: 12, color: "#374151",
                }}>{safeRaw}</pre>
              )}
              {!safeHtml && !safeText && !safeRaw && (
                <div style={{ padding: 24, color: "#6b7280", fontSize: 13 }}>
                  No body content returned by Postmark.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "4px 0", alignItems: "flex-start" }}>
      <div style={{ width: 72, color: "#6b7280", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );
}

function ViewTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
        background: active ? "#1a3f6b" : "#fff",
        color: active ? "#fff" : "#374151",
        border: `1px solid ${active ? "#1a3f6b" : "#e5e7eb"}`,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
